/**
 * Agent Login Page - PIN-based login for Assisted Booking Agents
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";

const AgentLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("email");

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setStep("pin");
  };

  const handlePinChange = (index, value) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    if (value && index < 3) { const nextInput = document.getElementById(`pin-${index + 1}`); if (nextInput) nextInput.focus(); }
    if (index === 3 && value) {
      const fullPin = [...newPin.slice(0, 3), value].join("");
      if (fullPin.length === 4) handleLogin(fullPin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) { const prevInput = document.getElementById(`pin-${index - 1}`); if (prevInput) prevInput.focus(); }
  };

  const handleLogin = async (fullPin) => {
    const pinToUse = fullPin || pin.join("");
    if (pinToUse.length !== 4) { toast.error("Please enter the full 4-digit PIN"); return; }
    try {
      setLoading(true);
      const response = await api.post("/assisted-booking-agents/login", { email: email.toLowerCase(), pin: pinToUse });
      if (response.data.success) {
        localStorage.setItem("agent_token", response.data.token);
        localStorage.setItem("agent_info", JSON.stringify(response.data.agent));
        toast.success(`Welcome, ${response.data.agent.name}!`);
        navigate("/admin/assisted-booking");
      }
    } catch (error) {
      console.error("Login failed:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
      setPin(["", "", "", ""]);
      document.getElementById("pin-0")?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="agent-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4"><svg className="h-8 w-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg></div>
          <h1 className="text-2xl font-bold text-white">Assisted Booking Portal</h1>
          <p className="text-slate-400 mt-1">Staff Login</p>
        </div>
        <div className="bg-slate-800 rounded-2xl p-6 shadow-xl">
          {step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div><label className="block text-slate-400 text-sm mb-2">Email Address</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white text-lg focus:outline-none focus:border-amber-500 transition-colors" placeholder="your.email@liantiaoja.com" autoFocus data-testid="agent-email-input" /></div>
              <button type="submit" className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-xl transition-colors" data-testid="continue-to-pin-btn">Continue</button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="text-center"><p className="text-slate-400 text-sm">Enter PIN for</p><p className="text-white font-medium">{email}</p><button onClick={() => { setStep("email"); setPin(["", "", "", ""]); }} className="text-amber-500 text-sm hover:text-amber-400 mt-1">Change email</button></div>
              <div className="flex justify-center gap-3">{pin.map((digit, index) => (<input key={index} id={`pin-${index}`} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={(e) => handlePinChange(index, e.target.value)} onKeyDown={(e) => handleKeyDown(index, e)} className="w-14 h-16 text-center text-2xl font-bold bg-slate-700 border-2 border-slate-600 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors" autoFocus={index === 0} disabled={loading} data-testid={`pin-input-${index}`} />))}</div>
              {loading && <div className="flex items-center justify-center gap-2 text-slate-400"><svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Verifying...</span></div>}
              <button onClick={() => handleLogin()} disabled={loading || pin.join("").length !== 4} className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="agent-login-btn">{loading ? "Signing In..." : "Sign In"}</button>
            </div>
          )}
        </div>
        <p className="text-center text-slate-500 text-sm mt-6">Need help? Contact your supervisor for PIN assistance.</p>
      </div>
    </div>
  );
};

export default AgentLogin;
